import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * Context pour la gestion du thème du club (couleurs, logo)
 * Les couleurs sont injectées en CSS variables pour une utilisation globale
 */

// Couleurs par défaut (Valencia App)
const DEFAULT_THEME = {
  primaryColor: '#F25F29',
  secondaryColor: '#FF7043',
  logo: null,
  clubName: 'Valencia App'
};

// Créer le contexte
const ClubThemeContext = createContext(null);

/**
 * Génère une couleur plus claire (pour les hovers, backgrounds légers)
 */
const lightenColor = (hex, percent) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
};

/**
 * Génère une couleur plus foncée (pour les états actifs, bordures)
 */
const darkenColor = (hex, percent) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) - amt;
  const G = (num >> 8 & 0x00FF) - amt;
  const B = (num & 0x0000FF) - amt;
  return '#' + (
    0x1000000 +
    (R > 0 ? R : 0) * 0x10000 +
    (G > 0 ? G : 0) * 0x100 +
    (B > 0 ? B : 0)
  ).toString(16).slice(1);
};

/**
 * Injecte les CSS variables dans le document
 */
const injectCSSVariables = (theme) => {
  const root = document.documentElement;

  // Couleurs principales
  root.style.setProperty('--club-primary', theme.primaryColor);
  root.style.setProperty('--club-secondary', theme.secondaryColor);

  // Variantes de la couleur primaire
  root.style.setProperty('--club-primary-light', lightenColor(theme.primaryColor, 20));
  root.style.setProperty('--club-primary-lighter', lightenColor(theme.primaryColor, 40));
  root.style.setProperty('--club-primary-dark', darkenColor(theme.primaryColor, 15));
  root.style.setProperty('--club-primary-darker', darkenColor(theme.primaryColor, 30));

  // Variantes de la couleur secondaire
  root.style.setProperty('--club-secondary-light', lightenColor(theme.secondaryColor, 20));
  root.style.setProperty('--club-secondary-dark', darkenColor(theme.secondaryColor, 15));

  // Gradient principal (pour AppBar, Drawer, etc.)
  root.style.setProperty(
    '--club-gradient',
    `linear-gradient(135deg, ${theme.primaryColor} 0%, ${theme.secondaryColor} 100%)`
  );

  // Gradient vertical (pour sidebars)
  root.style.setProperty(
    '--club-gradient-vertical',
    `linear-gradient(180deg, ${theme.primaryColor} 0%, ${darkenColor(theme.primaryColor, 20)} 100%)`
  );

  // Couleurs avec transparence
  root.style.setProperty('--club-primary-alpha-10', `${theme.primaryColor}1A`);
  root.style.setProperty('--club-primary-alpha-20', `${theme.primaryColor}33`);
  root.style.setProperty('--club-primary-alpha-50', `${theme.primaryColor}80`);
};

/**
 * Provider pour le thème du club
 */
export const ClubThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Charger depuis localStorage au démarrage
    const savedTheme = localStorage.getItem('clubTheme');
    if (savedTheme) {
      try {
        return JSON.parse(savedTheme);
      } catch {
        return DEFAULT_THEME;
      }
    }
    return DEFAULT_THEME;
  });

  // Injecter les CSS variables quand le thème change
  useEffect(() => {
    injectCSSVariables(theme);
  }, [theme]);

  /**
   * Met à jour le thème du club
   */
  const updateTheme = useCallback((newTheme) => {
    const updatedTheme = {
      ...DEFAULT_THEME,
      ...newTheme
    };
    setTheme(updatedTheme);
    localStorage.setItem('clubTheme', JSON.stringify(updatedTheme));
  }, []);

  /**
   * Réinitialise au thème par défaut
   */
  const resetTheme = useCallback(() => {
    setTheme(DEFAULT_THEME);
    localStorage.removeItem('clubTheme');
  }, []);

  /**
   * Retourne les styles pour un composant donné
   */
  const getComponentStyles = useCallback((component) => {
    switch (component) {
      case 'appBar':
        return {
          background: `var(--club-gradient)`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        };
      case 'drawer':
        return {
          background: `var(--club-gradient-vertical)`,
        };
      case 'button':
        return {
          backgroundColor: `var(--club-primary)`,
          '&:hover': {
            backgroundColor: `var(--club-primary-dark)`,
          },
        };
      case 'chip':
        return {
          backgroundColor: `var(--club-primary)`,
          color: 'white',
        };
      case 'menuItemActive':
        return {
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          color: '#FFCC80',
        };
      case 'menuItemHover':
        return {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          color: '#FFCC80',
        };
      default:
        return {};
    }
  }, []);

  const value = {
    theme,
    updateTheme,
    resetTheme,
    getComponentStyles,
    // Exposer les couleurs directement pour faciliter l'accès
    primaryColor: theme.primaryColor,
    secondaryColor: theme.secondaryColor,
    logo: theme.logo,
    clubName: theme.clubName,
  };

  return (
    <ClubThemeContext.Provider value={value}>
      {children}
    </ClubThemeContext.Provider>
  );
};

/**
 * Hook pour utiliser le thème du club
 */
export const useClubTheme = () => {
  const context = useContext(ClubThemeContext);
  if (!context) {
    throw new Error('useClubTheme must be used within a ClubThemeProvider');
  }
  return context;
};

export default ClubThemeContext;
